<template>
  <div class="w-100 flex-row justify-center">
    <div
      class="bg-surface pa-3 ma-1 align-center justify-center flex-column rounded"
      style="width: 55rem; position: relative"
    >
      <h1 class="text-h4">Retirement Income Planning Worksheet</h1>
      <table class="rounded w-100 mt-6 calculator-table">
        <thead>
          <tr>
            <th>Monthly Essential Retirement Expenses</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in essentialRetirementExpenses" :key="item.text">
            <td>{{ item.text }}</td>
            <td>
              <currency-input
                v-model="item.value"
                hide-details
                :autofocus="item.autofocus"
                :data-testid="`input-${item.text}`"
              />
            </td>
          </tr>
          <tr>
            <td>Total Essential Expenses</td>
            <td>{{ currencyFormat(totalEssentialExpenses, 0) }}</td>
          </tr>
        </tbody>
      </table>
      <table class="rounded w-100 mt-6 calculator-table break-after">
        <thead>
          <tr>
            <th>Monthly Lifestyle Retirement Expenses</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in lifestyleExpenses" :key="item.text">
            <td>{{ item.text }}</td>
            <td>
              <currency-input
                v-model="item.value"
                hide-details
                :data-testid="`input-${item.text}`"
              />
            </td>
          </tr>
          <tr>
            <td>Total Lifestyle Expenses</td>
            <td>{{ currencyFormat(totalLifestyleExpenses, 0) }}</td>
          </tr>
        </tbody>
      </table>
      <table class="rounded w-100 mt-6 calculator-table">
        <thead>
          <tr>
            <th>Monthly Sources of Guaranteed Lifetime Income</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in guaranteedIncome" :key="item.text">
            <td>{{ item.text }}</td>
            <td>
              <currency-input
                v-model="item.value"
                hide-details
                :data-testid="`input-${item.text}`"
              />
            </td>
          </tr>
          <tr>
            <td>Total Guaranteed Income</td>
            <td>{{ currencyFormat(totalGuaranteedIncome, 0) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="flex-column align-end w-100 mt-6">
        <div
          v-if="essentialLivingIncomeGap > 0"
          data-testid="living-income-gap"
          class="bg-success rounded pa-3 ma-1"
        >
          No essential living income gap. (Excess of
          {{ currencyFormat(essentialLivingIncomeGap, 0) }})
        </div>
        <div
          v-else-if="essentialLivingIncomeGap < 0"
          data-testid="living-income-gap"
          class="bg-error rounded pa-3 ma-1"
        >
          Essential living income gap. (Deficit of
          {{ currencyFormat(essentialLivingIncomeGap * -1, 0) }})
        </div>
        <div
          v-else
          data-testid="living-income-gap"
          class="bg-success rounded pa-3 ma-1"
        >
          You are able to meet your monthly essential living expenses
        </div>

        <div
          v-if="totalRetirementIncomeGap > 0"
          data-testid="retirement-income-gap"
          class="bg-success rounded pa-3 ma-1"
        >
          Total retirement expenses income gap. (Excess of
          {{ currencyFormat(totalRetirementIncomeGap, 0) }})
        </div>
        <div
          v-else-if="totalRetirementIncomeGap < 0"
          data-testid="retirement-income-gap"
          class="bg-error rounded pa-3 ma-1"
        >
          Total retirement expenses income gap. (Deficit of
          {{ currencyFormat(totalRetirementIncomeGap * -1, 0) }})
        </div>
        <div
          v-else
          data-testid="retirement-income-gap"
          class="bg-success rounded pa-3 ma-1"
        >
          You are able to meet your total retirement expenses
        </div>
      </div>

      <table class="rounded w-100 mt-6 calculator-table">
        <thead>
          <tr>
            <th>Non-Guaranteed Retirement Income Sources</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in nonGuaranteedRetirementIncomeSources"
            :key="item.text"
          >
            <td>{{ item.text }}</td>
            <td>
              <currency-input
                v-model="item.value"
                hide-details
                :data-testid="`input-${item.text}`"
              />
            </td>
          </tr>
          <tr>
            <td>Total Retirement Savings</td>
            <td>{{ currencyFormat(totalRetirementSavings, 0) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="flex-column align-end w-100">
        <div
          data-testid="portfolio-withdrawal-rate"
          class="bg-info rounded pa-3 ma-1"
        >
          Expected portfolio withdrawal rate
          {{ (withdrawalRate * 100).toFixed(0) }}%
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import CurrencyInput from "@/components/shared/CurrencyInput.vue";
import { currencyFormat } from "@/util/helpers";
import { useHead } from "@unhead/vue";
import { computed, ref } from "vue";

useHead({ title: "Retirement Income Calculator" });

const essentialRetirementExpenses = ref({
  housing: {
    text: "Housing",
    value: null,
    autofocus: true
  },
  utilities: {
    text: "Utilities",
    value: null
  },
  groceries: {
    text: "Groceries",
    value: null
  },
  transportation: {
    text: "Transportation",
    value: null
  },
  personalCare: {
    text: "Personal Care",
    value: null
  },
  premiums: {
    text: "Health Care and Medicare Premiums",
    value: null
  },
  insurance: {
    text: "Life, Disability, and LTC Insurance",
    value: null
  },
  otherExpenses: {
    text: "Other Mandatory Expenses",
    value: null
  }
});

const lifestyleExpenses = ref({
  restaurantsAndDining: {
    text: "Restaurants and Dining",
    value: null
  },
  entertainment: {
    text: "Entertainment",
    value: null
  },
  travelAndRecreation: {
    text: "Travel and Recreation",
    value: null
  },
  memberships: {
    text: "Memberships",
    value: null
  },
  hobbiesAndInterests: {
    text: "Hobbies/Interests",
    value: null
  },
  giftsAndDonations: {
    text: "Gifts to Family and Charitable Donations",
    value: null
  },
  otherExpenses: {
    text: "Other Discretionary Expenses",
    value: null
  }
});

const guaranteedIncome = ref({
  socialSecurity: {
    text: "Social Security",
    value: null
  },
  pensions: {
    text: "Pensions",
    value: null
  },
  annuities: {
    text: "Annuities",
    value: null
  },
  passiveIncome: {
    text: "Passive Business/Rental Income",
    value: null
  },
  other: {
    text: "Other",
    value: null
  },
  otherMandatoryExpenses: {
    text: "Other Mandatory Expenses",
    value: null
  }
});

const nonGuaranteedRetirementIncomeSources = ref({
  employerRetirementPlans: {
    text: "Employer Retirement Plans",
    value: null
  },
  individualRetirementAccounts: {
    text: "Individual Retirement Accounts",
    value: null
  },
  investmentAccounts: {
    text: "Investment Accounts",
    value: null
  },
  savingsAndCDs: {
    text: "Savings, CDs (excluding Emergency Fund)",
    value: null
  },
  cashValueOfLifeInsurance: {
    text: "Cash Value of Life Insurance",
    value: null
  },
  other: {
    text: "Other",
    value: null
  }
});

const totalEssentialExpenses = computed(() =>
  Object.values(essentialRetirementExpenses.value).reduce(
    (acc, i) => acc + (i.value || 0),
    0
  )
);
const totalLifestyleExpenses = computed(() =>
  Object.values(lifestyleExpenses.value).reduce(
    (acc, i) => acc + (i.value || 0),
    0
  )
);
const totalGuaranteedIncome = computed(() =>
  Object.values(guaranteedIncome.value).reduce(
    (acc, i) => acc + (i.value || 0),
    0
  )
);
const totalRetirementSavings = computed(() =>
  Object.values(nonGuaranteedRetirementIncomeSources.value).reduce(
    (acc, i) => acc + (i.value || 0),
    0
  )
);

const essentialLivingIncomeGap = computed(
  () => totalGuaranteedIncome.value - totalEssentialExpenses.value
);

const totalRetirementIncomeGap = computed(
  () =>
    totalGuaranteedIncome.value -
    (totalEssentialExpenses.value + totalLifestyleExpenses.value)
);

const withdrawalRate = computed(() => {
  if (totalRetirementSavings.value === 0) return 0;
  return Math.abs(
    (totalRetirementIncomeGap.value * 12) / totalRetirementSavings.value
  );
});
</script>

<style lang="scss">
.calculator-table {
  border-collapse: collapse;

  td {
    height: 48px;
  }

  tbody tr td {
    padding: 9px;
  }

  tbody tr:nth-child(odd) {
    background-color: #ececec;
  }

  thead {
    background-color: rgb(var(--v-theme-success));
    color: white;
  }
}

@media print {
  .break-after {
    page-break-after: always;
  }
}
</style>
